import axios from "axios";
import { getDeviceFingerprint, getDeviceId } from "../utils/deviceId";
import { LANDING_ONLY } from "../config/launchMode";
import {
  clearLegacySessionTokens,
  USER_SESSION_POLICY,
} from "./sessionPolicy";

const DEFAULT_API_BASE_URL = "https://api.aethercv.com/api";
const USER_STORAGE_KEY = USER_SESSION_POLICY.snapshotStorageKey;
const USER_SESSION_HINT_KEY = USER_SESSION_POLICY.sessionHintKey;
const USER_SESSION_EVENT = USER_SESSION_POLICY.sessionEvent;

const normalizeApiUrl = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

const resolveApiBaseUrl = () => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
    const envUrl = normalizeApiUrl(import.meta.env.VITE_API_URL);
    if (envUrl) return envUrl;
  }
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    return "/api";
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return DEFAULT_API_BASE_URL;
};

export const API_BASE_URL = resolveApiBaseUrl().replace(/\/$/, "");
const DEFAULT_CACHE_TTL = 60000;
const DEFAULT_STALE_TTL = 5 * 60 * 1000;
const CACHE_INVALIDATION_EVENT = "aethercv-cache-invalidated";
const CACHE_BROADCAST_CHANNEL = "aethercv-cache";
const requestCache = new Map();
const SESSION_CACHE_PREFIX = "aethercv-cache:";
const PERSIST_CACHE_PREFIXES = [
  "/user/me",
  "/user/status",
  "/user/job-applications",
  "/interview/plans",
  "/interview/roles",
  "/resume/list",
  "/resume/get",
  "/user/billing/history",
];
export const USER_PROFILE_QUERY = Object.freeze({ params: { view: "fast" } });

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const canUseLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

clearLegacySessionTokens();

const readJsonFromStorage = (key) => {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJsonToStorage = (key, value) => {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write errors.
  }
};

const removeStorageKey = (key) => {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage removal errors.
  }
};

const normalizeUserSnapshot = (user) => {
  if (!user || typeof user !== "object") return null;
  const id = String(user.id || user._id || "").trim();
  if (!id) return null;
  return {
    id,
    name: String(user.name || "").trim(),
    email: String(user.email || "").trim(),
    avatar: typeof user.avatar === "string" ? user.avatar : "",
    isEmailVerified: Boolean(user.isEmailVerified),
    plan:
      typeof user.plan === "string"
        ? user.plan
        : typeof user.subscription?.plan === "string"
          ? user.subscription.plan
          : "",
  };
};

const notifyUserSessionChanged = (reason) => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(USER_SESSION_EVENT, {
        detail: {
          reason,
          user: readJsonFromStorage(USER_STORAGE_KEY),
          hasSession: Boolean(
            window.localStorage.getItem(USER_SESSION_HINT_KEY) === "1"
          ),
        },
      })
    );
  } catch {
    // Ignore CustomEvent failures.
  }
};

const clearSessionCookies = async (path) => {
  try {
    await fetch(`${API_BASE_URL}${path}`, { method: "POST", credentials: "include" });
  } catch {
    // Best-effort cookie cleanup.
  }
};

export const getStoredUser = () => readJsonFromStorage(USER_STORAGE_KEY);

export const hasUserSessionHint = () => {
  if (!canUseLocalStorage()) return false;
  return (
    window.localStorage.getItem(USER_SESSION_HINT_KEY) === "1" ||
    Boolean(getStoredUser()?.id)
  );
};

export const setStoredUser = (user, options = {}) => {
  const snapshot = normalizeUserSnapshot(user);
  if (snapshot) {
    writeJsonToStorage(USER_STORAGE_KEY, snapshot);
    if (canUseLocalStorage()) {
      try {
        window.localStorage.setItem(USER_SESSION_HINT_KEY, "1");
      } catch {
        // Ignore storage write errors.
      }
    }
  } else {
    removeStorageKey(USER_STORAGE_KEY);
    removeStorageKey(USER_SESSION_HINT_KEY);
  }
  if (!options.silent) {
    notifyUserSessionChanged(options.reason || (snapshot ? "set" : "clear"));
  }
  return snapshot;
};

export const syncStoredUserFromProfile = (profile, options = {}) =>
  setStoredUser(profile, options);

export const clearUserSession = async (options = {}) => {
  if (options.server !== false) {
    await clearSessionCookies(USER_SESSION_POLICY.logoutPath);
  }
  clearLegacySessionTokens();
  removeStorageKey(USER_STORAGE_KEY);
  removeStorageKey(USER_SESSION_HINT_KEY);
  notifyUserSessionChanged(options.reason || "logout");
};

api.interceptors.request.use((config) => {
  const deviceId = getDeviceId();
  if (deviceId) {
    config.headers = config.headers || {};
    config.headers["X-Device-Id"] = deviceId;
  }
  const deviceFingerprint = getDeviceFingerprint();
  if (deviceFingerprint) {
    config.headers = config.headers || {};
    config.headers["X-Device-Fingerprint"] = deviceFingerprint;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if ((status === 401 || status === 403) && typeof window !== "undefined") {
      if (hasUserSessionHint()) {
        void clearUserSession({ server: false, reason: "expired" });
        if (!LANDING_ONLY) {
          const pathname = window.location?.pathname || "";
          if (pathname !== "/login") {
            window.location.assign("/login");
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

const buildCacheKey = (url, config = {}) => {
  const params = config?.params ? JSON.stringify(config.params) : "";
  const userId = getStoredUser()?.id || "anon";
  return `${userId}|${url}|${params}`;
};

const canUseSessionStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const shouldPersist = (url) =>
  PERSIST_CACHE_PREFIXES.some((prefix) => url.startsWith(prefix));

const toSessionKey = (cacheKey) => `${SESSION_CACHE_PREFIX}${cacheKey}`;
let broadcastChannel = null;

const getBroadcastChannel = () => {
  if (typeof window === "undefined" || !("BroadcastChannel" in window)) return null;
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(CACHE_BROADCAST_CHANNEL);
  }
  return broadcastChannel;
};

const notifyCacheInvalidated = (payload) => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CACHE_INVALIDATION_EVENT, { detail: payload }));
  } catch {
    // Ignore CustomEvent failures.
  }

  try {
    const channel = getBroadcastChannel();
    if (channel) {
      channel.postMessage(payload);
    } else if (typeof window.localStorage !== "undefined") {
      window.localStorage.setItem(
        CACHE_INVALIDATION_EVENT,
        JSON.stringify({ ...payload, ts: Date.now() })
      );
    }
  } catch {
    // Ignore broadcast failures.
  }
};

const getUrlFromCacheKey = (cacheKey) => {
  const parts = String(cacheKey || "").split("|");
  return parts.length >= 3 ? parts[1] : "";
};

const matchesInvalidationTarget = (cacheKey, targets = []) => {
  const url = getUrlFromCacheKey(cacheKey);
  if (!url) return false;
  return targets.some((prefix) => url.startsWith(prefix));
};

const readPersisted = (cacheKey) => {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(toSessionKey(cacheKey));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writePersisted = (cacheKey, payload) => {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(toSessionKey(cacheKey), JSON.stringify(payload));
  } catch {
    // Ignore storage write errors (quota, disabled).
  }
};

export const getCachedSnapshot = (url, config = {}, options = {}) => {
  const cacheKey = buildCacheKey(url, config);
  const maxAge = options.maxAge ?? DEFAULT_CACHE_TTL;
  const now = Date.now();
  const cached = requestCache.get(cacheKey);
  if (cached?.data && now - cached.timestamp <= maxAge) return cached.data;
  const persisted = readPersisted(cacheKey);
  if (persisted?.data && now - persisted.timestamp <= maxAge) return persisted.data;
  return null;
};

export const getCached = async (url, config = {}, options = {}) => {
  const allowStale = options.allowStale ?? shouldPersist(url);
  const staleTtl = options.staleTtl ?? DEFAULT_STALE_TTL;
  const { ttl = DEFAULT_CACHE_TTL, force = false, key } = options;
  const cacheKey = key || buildCacheKey(url, config);
  const now = Date.now();
  const cached = requestCache.get(cacheKey);

  if (!force && cached) {
    if (cached.data && now - cached.timestamp < ttl) {
      return cached.data;
    }
    if (cached.promise) {
      return cached.promise;
    }
  }

  if (!force && !cached) {
    const persisted = readPersisted(cacheKey);
    if (persisted?.data && now - persisted.timestamp < ttl) {
      requestCache.set(cacheKey, { data: persisted.data, timestamp: persisted.timestamp });
      return persisted.data;
    }
  }

  const requestPromise = api
    .get(url, config)
    .then((res) => {
      const timestamp = Date.now();
      const payload = { data: res.data, timestamp };
      requestCache.set(cacheKey, payload);
      if (shouldPersist(url)) {
        writePersisted(cacheKey, payload);
      }
      return res.data;
    })
    .catch((err) => {
      requestCache.delete(cacheKey);
      const isNetworkFailure = !err?.response;
      if (allowStale && isNetworkFailure) {
        const fallback = cached?.data ? cached : readPersisted(cacheKey);
        const fallbackNow = Date.now();
        if (fallback?.data && fallbackNow - fallback.timestamp < staleTtl) {
          requestCache.set(cacheKey, fallback);
          return fallback.data;
        }
      }
      throw err;
    });

  requestCache.set(cacheKey, { promise: requestPromise, timestamp: now });
  return requestPromise;
};

export const getUserProfileSnapshot = (options = {}) =>
  getCachedSnapshot("/user/me", USER_PROFILE_QUERY, options);

export const getUserProfile = async (options = {}) => {
  const data = await getCached("/user/me", USER_PROFILE_QUERY, options);
  syncStoredUserFromProfile(data, { silent: true });
  return data;
};

export const getUserStatus = (options = {}) => getCached("/user/status", {}, options);

export const invalidateCache = (prefixes) => {
  if (!prefixes) {
    requestCache.clear();
    if (canUseSessionStorage()) {
      Object.keys(window.sessionStorage).forEach((key) => {
        if (key.startsWith(SESSION_CACHE_PREFIX)) {
          window.sessionStorage.removeItem(key);
        }
      });
    }
    notifyCacheInvalidated({ prefixes: "*", ts: Date.now() });
    return;
  }

  const targets = Array.isArray(prefixes) ? prefixes : [prefixes];
  for (const key of requestCache.keys()) {
    if (matchesInvalidationTarget(key, targets)) {
      requestCache.delete(key);
    }
  }
  if (canUseSessionStorage()) {
    Object.keys(window.sessionStorage).forEach((key) => {
      if (!key.startsWith(SESSION_CACHE_PREFIX)) return;
      const rawKey = key.slice(SESSION_CACHE_PREFIX.length);
      if (matchesInvalidationTarget(rawKey, targets)) {
        window.sessionStorage.removeItem(key);
      }
    });
  }
  notifyCacheInvalidated({ prefixes: targets, ts: Date.now() });
};

export const buildApiUrl = (path = "") => {
  if (!path) return API_BASE_URL;
  return `${API_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
};

const toAbsoluteApiUrl = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (typeof window === "undefined" || !window.location?.origin) {
    return rawValue;
  }
  try {
    return new URL(rawValue, window.location.origin).toString();
  } catch {
    return rawValue;
  }
};

const getUrlOrigin = (value) => {
  const absoluteUrl = toAbsoluteApiUrl(value);
  if (!absoluteUrl) return "";
  try {
    return new URL(absoluteUrl).origin;
  } catch {
    return "";
  }
};

const shouldUseWindowOriginApiCandidate = () =>
  typeof window !== "undefined" &&
  Boolean(window.location?.origin) &&
  /^https?:/i.test(String(window.location.origin || "")) &&
  (
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    getUrlOrigin(API_BASE_URL) === window.location.origin
  );

export const buildApiUrlCandidates = (path = "") => {
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  const candidates = [];

  candidates.push(toAbsoluteApiUrl(buildApiUrl(normalizedPath)));

  if (shouldUseWindowOriginApiCandidate()) {
    candidates.push(toAbsoluteApiUrl(`/api${normalizedPath ? `/${normalizedPath}` : ""}`));
  }

  return [...new Set(candidates.filter(Boolean))];
};

export const buildAuthenticatedHeaders = (headers = {}) => {
  const nextHeaders = new Headers(headers);
  const deviceId = getDeviceId();
  if (deviceId && !nextHeaders.has("X-Device-Id")) {
    nextHeaders.set("X-Device-Id", deviceId);
  }
  const deviceFingerprint = getDeviceFingerprint();
  if (deviceFingerprint && !nextHeaders.has("X-Device-Fingerprint")) {
    nextHeaders.set("X-Device-Fingerprint", deviceFingerprint);
  }
  return nextHeaders;
};

export const authFetch = (path, options = {}) => {
  const { headers, credentials = "include", ...rest } = options;
  const url =
    typeof path === "string" && /^https?:\/\//i.test(path) ? path : buildApiUrl(path);

  return fetch(url, {
    ...rest,
    credentials,
    headers: buildAuthenticatedHeaders(headers),
  });
};

export const subscribeToUserSession = (handler) => {
  if (typeof window === "undefined") return () => {};
  const safeHandler = (detail) => {
    if (typeof handler !== "function") return;
    handler(detail || { user: getStoredUser(), hasSession: hasUserSessionHint() });
  };

  const handleCustomEvent = (event) => safeHandler(event.detail);
  const handleStorageEvent = (event) => {
    if (![USER_STORAGE_KEY, USER_SESSION_HINT_KEY].includes(event.key)) return;
    safeHandler({ user: getStoredUser(), hasSession: hasUserSessionHint() });
  };

  window.addEventListener(USER_SESSION_EVENT, handleCustomEvent);
  window.addEventListener("storage", handleStorageEvent);
  return () => {
    window.removeEventListener(USER_SESSION_EVENT, handleCustomEvent);
    window.removeEventListener("storage", handleStorageEvent);
  };
};

export const subscribeToCacheInvalidation = (handler) => {
  if (typeof window === "undefined") return () => {};
  const safeHandler = (payload) => {
    if (typeof handler !== "function") return;
    handler(payload);
  };

  const handleCustomEvent = (event) => safeHandler(event.detail);
  const handleStorageEvent = (event) => {
    if (event.key !== CACHE_INVALIDATION_EVENT || !event.newValue) return;
    try {
      safeHandler(JSON.parse(event.newValue));
    } catch {
      // Ignore invalid JSON payloads.
    }
  };
  window.addEventListener(CACHE_INVALIDATION_EVENT, handleCustomEvent);
  window.addEventListener("storage", handleStorageEvent);

  const channel = getBroadcastChannel();
  const handleChannelMessage = (event) => safeHandler(event.data);
  if (channel) {
    channel.addEventListener("message", handleChannelMessage);
  }

  return () => {
    window.removeEventListener(CACHE_INVALIDATION_EVENT, handleCustomEvent);
    window.removeEventListener("storage", handleStorageEvent);
    if (channel) {
      channel.removeEventListener("message", handleChannelMessage);
    }
  };
};

export default api;
